import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Spinner, Alert, Form, Badge,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'التصنيفات', icon: 'bi-folder' },
];

const CATEGORY_COLORS = [
  '#0d6efd', '#6f42c1', '#d63384', '#dc3545',
  '#fd7e14', '#198754', '#20c997', '#0dcaf0',
  '#ffc107', '#6c757d',
];

const categoryColor = (name, idx) =>
  CATEGORY_COLORS[idx % CATEGORY_COLORS.length];

const CategoryCard = ({ category, index }) => {
  const voCount = category.mjalis_vos?.length ?? 0;
  const ytCount = category.mjalis_yts?.length ?? 0;
  const total   = voCount + ytCount;
  const color   = categoryColor(category.Name, index);

  return (
    <Card className="h-100 border-0 shadow-sm hover-card">
      <Card.Body className="d-flex flex-column align-items-center text-center p-4">
        {/* Icon */}
        <div
          style={{
            width: 72, height: 72, borderRadius: 16,
            background: color + '22', border: `2px solid ${color}33`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16, flexShrink: 0,
          }}
        >
          <i className="bi bi-folder-fill" style={{ fontSize: '2rem', color }} />
        </div>

        {/* Name */}
        <Card.Title
          className="mb-3 fw-bold"
          style={{ fontSize: '1rem', color: '#212529', lineHeight: 1.4 }}
        >
          {category.Name}
        </Card.Title>

        {/* Stats badges */}
        <div className="d-flex gap-2 mt-auto flex-wrap justify-content-center">
          {voCount > 0 && (
            <Badge bg="primary" style={{ fontSize: '0.75rem' }}>
              <i className="bi bi-camera-video ms-1" />
              {voCount} محاضرة
            </Badge>
          )}
          {ytCount > 0 && (
            <Badge bg="danger" style={{ fontSize: '0.75rem' }}>
              <i className="bi bi-youtube ms-1" />
              {ytCount} يوتيوب
            </Badge>
          )}
          {total === 0 && (
            <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>لا توجد محاضرات</Badge>
          )}
        </div>
      </Card.Body>

      {total > 0 && (
        <Card.Footer
          className="text-center text-muted border-0 pt-0 pb-3"
          style={{ background: 'transparent', fontSize: '0.8rem' }}
        >
          الإجمالي: {total} تسجيل
        </Card.Footer>
      )}
    </Card>
  );
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getCategories({ search });
      setCategories(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'تعذّر تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const totalLectures = categories.reduce(
    (sum, c) => sum + (c.mjalis_vos?.length ?? 0) + (c.mjalis_yts?.length ?? 0), 0
  );

  return (
    <Layout title="التصنيفات" breadcrumbs={breadcrumbs}>
      <Container fluid dir="rtl">

        {/* Stats */}
        <Row className="mb-4 g-3">
          {[
            { label: 'إجمالي التصنيفات', value: categories.length, icon: 'bi-folder-fill', color: '#0d6efd' },
            { label: 'إجمالي التسجيلات', value: totalLectures, icon: 'bi-collection-fill', color: '#6f42c1' },
          ].map(s => (
            <Col md={3} sm={6} key={s.label}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center gap-3 p-3">
                  <div style={{
                    width: 54, height: 54, borderRadius: 12,
                    background: s.color + '22', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className={s.icon} style={{ fontSize: '1.4rem', color: s.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
                    <div className="text-muted small mt-1">{s.label}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Search */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Form.Control
              placeholder="بحث في التصنيفات..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </Card.Body>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle-fill ms-2" />
            {error}
          </Alert>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="text-muted mt-3">جارٍ تحميل التصنيفات...</div>
          </div>
        ) : categories.length === 0 ? (
          <Alert variant="info" className="text-center">
            <i className="bi bi-folder-x ms-2" />
            لا توجد نتائج مطابقة
          </Alert>
        ) : (
          <Row className="g-3">
            {categories.map((cat, idx) => (
              <Col xl={2} lg={3} md={4} sm={6} key={cat.documentId}>
                <CategoryCard category={cat} index={idx} />
              </Col>
            ))}
          </Row>
        )}
      </Container>

      <style>{`
        .hover-card { transition: all 0.25s ease; }
        .hover-card:hover { transform: translateY(-4px); box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,0.12) !important; }
      `}</style>
    </Layout>
  );
};

export default Categories;
